import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  makeStyles,
  TextField
} from "@material-ui/core";
import { Close, DeleteOutlined } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { useSendEmail } from "../../hooks/useSendEmail";
import { actionShowSpinner } from "../../redux-flow/action/Wrapper/Spinner";
import Textarea from "../Common/Textarea";
import ToastMessage from "../Common/ToastMessage";
import {
  ActionButtons,
  CloseButton,
  Container,
  Dropzone,
  FilesContainer,
  FormSection,
  Header,
  SlotsTable,
  SlotsTableHeader,
  SlotsTableRow,
} from "./styles";

const useStyles = makeStyles((theme) => ({
  button: {
    fontSize: "1rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 150,
    minHeight: 45,
  },
  buttonOutlined: {
    border: "2px solid",
    fontSize: "1rem",
    fontWeight: "bold",
    color: theme.palette.grey.main,
    textTransform: "capitalize",
    minWidth: 150,
    minHeight: 45,
  },
}));

function BatchSlotConfirmModal({ slots, meetingInfo, onClose, onConfirm, t }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { sendBatchConfirmSlots } = useSendEmail({
    spinner: (val) => dispatch(actionShowSpinner(val)),
  });

  const [emails, setEmails] = useState("");
  const [filesAnexo, setFilesAnexo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlineMeet, setOnlineMeet] = useState(false);
  const [slotLocation, setSlotLocation] = useState("");
  const [fileOver, setFileOver] = useState(false);

  const [toastMessage, setToastMessage] = useState({
    message: "",
    type: "error",
    open: false,
  });

  useEffect(() => {
    const allEmails = new Set();

    slots.forEach((slot) => {
      (slot.hosts || [])
        .filter((host) => host.email)
        .forEach((host) => allEmails.add(host.email));

      const [recipients] = slot.slotConfirmationRecipients || [];
      if (recipients) {
        recipients
          .split(";")
          .filter((e) => e.trim())
          .forEach((e) => allEmails.add(e.trim()));
      }
    });

    setEmails(Array.from(allEmails).join(";"));
  }, [slots]);

  const getSlotName = (slot) => {
    if (slot.name && slot.name.props && slot.name.props.children) {
      return slot.name.props.children;
    }
    return slot.name || "";
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleCapture = async ({ target }) => {
    const listArquivos = [];

    for (const file of target.files) {
      const fileBase64 = await toBase64(file);

      listArquivos.push({
        file: file,
        id: uuidv4(),
        name: file.name,
        contentType: file.type,
        contentByte: fileBase64.split(",")[1],
      });
    }

    setFilesAnexo([...filesAnexo, ...listArquivos]);
  };

  const handleRemoveFile = (itemId) => {
    setFilesAnexo(filesAnexo.filter((file) => file.id !== itemId));
  };

  const dragOver = (e) => {
    setFileOver(true);
    e.preventDefault();
  };

  const dragEnter = (e) => {
    setFileOver(true);
    e.preventDefault();
  };

  const dragLeave = (e) => {
    setFileOver(false);
    e.preventDefault();
  };

  const fileDrop = async (e) => {
    setFileOver(false);
    e.preventDefault();

    const files = e.dataTransfer.files;
    const listArquivos = [];

    for (const file of files) {
      const fileBase64 = await toBase64(file);

      listArquivos.push({
        file: file,
        id: uuidv4(),
        name: file.name,
        contentType: file.type,
        contentByte: fileBase64.split(",")[1],
      });
    }

    setFilesAnexo([...filesAnexo, ...listArquivos]);
  };

  const handleConfirm = async () => {
    setLoading(true);

    const emailValidator =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    const receiptsToSend = emails
      .split(";")
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    if (receiptsToSend.length > 0) {
      let isEmailsValid = true;
      receiptsToSend.forEach((email) => {
        if (!emailValidator.test(String(email).toLowerCase())) {
          isEmailsValid = false;
        }
      });

      if (!isEmailsValid) {
        setToastMessage({
          open: true,
          message: t("batchSlotConfirm.invalidEmail"),
          type: "error",
        });
        setLoading(false);
        return;
      }
    }

    const formData = new FormData();

    formData.append("MeetId", meetingInfo.meetId);
    formData.append(
      "MeetDate",
      `${meetingInfo.meetDate.split("/")[2]}/${meetingInfo.meetDate.split("/")[1]}/${meetingInfo.meetDate.split("/")[0]}`
    );
    formData.append("LocationName", slotLocation);
    formData.append("OnlineMeetingProvider", onlineMeet);

    slots.forEach((slot, index) => {
      formData.append(`SlotIds[${index}]`, slot.id);
    });

    receiptsToSend.forEach((email, index) => {
      formData.append(`Recipients[${index}].Address`, email);
    });

    let count = 0;
    for (const file of filesAnexo) {
      formData.append(`Attachments[${count}].fromFiles`, file.file);
      formData.append(`Attachments[${count}].fileType`, file.contentType);
      count += 1;
    }

    const response = await sendBatchConfirmSlots(formData);

    setLoading(false);

    if (response?.error && response.error.length > 0) {
      setToastMessage({
        open: true,
        message: t("batchSlotConfirm.error"),
        type: "error",
      });
    } else {
      onConfirm(receiptsToSend);
    }
  };

  return (
    <Container>
      <Header>
        <h2>{t("batchSlotConfirm.title")}</h2>
        <CloseButton onClick={onClose}>
          <Close />
        </CloseButton>
      </Header>

      <SlotsTable>
        <SlotsTableHeader>
          <div>#</div>
          <div>{t("forms.startHour")}</div>
          <div>{t("forms.endHour")}</div>
          <div>{t("forms.themeName")}</div>
          <div>{t("meeting.type")}</div>
          <div>{t("slots.presenter")}</div>
        </SlotsTableHeader>

        {slots.map((slot, index) => (
          <SlotsTableRow key={slot.id} $index={index}>
            <div>{slot.order}</div>
            <div>{slot.meetStartTime}</div>
            <div>{slot.meetEndTime}</div>
            <div>{getSlotName(slot)}</div>
            <div>{t(`slots.slotType.${slot.slotType}`)}</div>
            <div>
              {slot.hosts && slot.hosts.length > 1
                ? slot.hosts.map((h) => h.userName).join(" | ")
                : slot.presenter}
            </div>
          </SlotsTableRow>
        ))}
      </SlotsTable>

      <FormSection>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Textarea
              name="batchEmails"
              id="batchEmails"
              label={`${t("forms.forLabel")} (${t("forms.optional")})`}
              onChange={(event) => setEmails(event.target.value)}
              value={emails}
            />
          </Grid>

          <Grid container item spacing={2}>
            {!onlineMeet && (
              <Grid item xs={6}>
                <TextField
                  name="batchPlace"
                  id="batchPlace"
                  autoComplete="nope"
                  fullWidth
                  label={t("batchSlotConfirm.location")}
                  value={slotLocation}
                  onChange={(event) => setSlotLocation(event.target.value)}
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    value={onlineMeet}
                    checked={onlineMeet}
                    onChange={() => setOnlineMeet(!onlineMeet)}
                    name="batchOnlineMeet"
                    id="batchOnlineMeet"
                    color="primary"
                  />
                }
                label={t("meeting.onlineMeet")}
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <center>
              <Button
                variant="contained"
                color="primary"
                component="label"
                style={{ width: "20%", marginTop: "2%" }}
              >
                {t("forms.attachFiles")}
                <input
                  type="file"
                  hidden
                  multiple
                  onClick={(event) => {
                    event.target.value = null;
                  }}
                  onChange={handleCapture}
                />
              </Button>

              <Dropzone
                onDragOver={dragOver}
                onDragEnter={dragEnter}
                onDragLeave={dragLeave}
                onDrop={fileDrop}
                style={fileOver ? { borderColor: "#465eb6" } : {}}
              >
                {t("forms.dragFilesHere")}
              </Dropzone>

              <Box
                style={{
                  display: "flex",
                  flex: 1,
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  marginTop: "16px",
                }}
              >
                <span style={{ fontSize: "12px", color: "#425AF4" }}>
                  {t("forms.checkFileAlert")}
                </span>
              </Box>

              <Grid item lg={12} xs={12} style={{ marginTop: 8 }}>
                {filesAnexo.map((item) => (
                  <FilesContainer key={item.id}>
                    <p>{item.name}</p>
                    <DeleteOutlined
                      onClick={() => handleRemoveFile(item.id)}
                    />
                  </FilesContainer>
                ))}
              </Grid>
            </center>
          </Grid>
        </Grid>
      </FormSection>

      <ActionButtons>
        <Button
          variant="contained"
          onClick={onClose}
          className={classes.buttonOutlined}
          disabled={loading}
        >
          {t("forms.committee.buttons.cancel")}
        </Button>

        <Button
          variant="contained"
          onClick={handleConfirm}
          className={classes.button}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("batchSlotConfirm.confirm")
          )}
        </Button>
      </ActionButtons>

      <ToastMessage
        message={toastMessage.message}
        type={toastMessage.type || "error"}
        open={toastMessage.open}
        close={() => setToastMessage({ ...toastMessage, open: false })}
      />
    </Container>
  );
}

export default BatchSlotConfirmModal;
